import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "../store";
import { BreadCrumbs } from "./BreadCrumbs";
import { getPreviewHtml } from "./dailyReportPreviewHtml";
import { notify } from "../util/notify";
import "./DailyReportPreview.scss";

type DailyReportPreviewData = {
  date: string;
  subject: string;
  body: string;
  recipients: string[];
  entryCount: number;
  willSend: boolean;
};

const getCurrentEasternDate = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((value) => value.type === type)?.value;
  const year = Number(part("year"));
  const month = Number(part("month"));
  const day = Number(part("day"));

  return `${year}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getSendErrorMessage = (error: unknown) => {
  if (isRecord(error) && isRecord(error.response)) {
    const response = error.response;
    const data = isRecord(response.data) ? response.data : undefined;
    const message = data?.message;
    if (typeof message === "string" && message !== "Service Error") {
      return `Could not send the daily report email: ${message}`;
    }

    if (typeof response.status === "number") {
      return `Could not send the daily report email (server error ${response.status}). Please contact an administrator if this continues.`;
    }
  }

  return "Could not send the daily report email. Please check your connection and try again.";
};

export default function DailyReportPreview() {
  const { invokeApi, group } = useUser();
  const [date, setDate] = useState(getCurrentEasternDate);
  const [preview, setPreview] = useState<DailyReportPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [sentRecipients, setSentRecipients] = useState<string[] | null>(null);
  const invokeApiRef = useRef(invokeApi);
  invokeApiRef.current = invokeApi;

  const fetchPreview = useCallback(async (selectedDate: string) => {
    setIsLoading(true);
    setError("");
    setPreview(null);
    setSendError("");
    setSentRecipients(null);

    try {
      const data = await invokeApiRef.current({
        endpoint: "/getDailyReportPreview",
        data: { date: selectedDate },
      });
      setPreview(data);
    } catch {
      setError("Could not load the daily report preview. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPreview(date);
  }, [date, fetchPreview]);

  const loadPreview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void fetchPreview(date);
  };

  const sendPreviewEmail = async () => {
    setIsSending(true);
    setSendError("");
    setSentRecipients(null);
    try {
      const result = await invokeApiRef.current({
        endpoint: "/sendDailyReportPreview",
        data: { date },
      });
      setSentRecipients(result.recipients);
      notify.success(`Daily report sent to ${result.recipients.length} recipient${result.recipients.length === 1 ? "" : "s"}.`);
    } catch (error: unknown) {
      setSendError(getSendErrorMessage(error));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <BreadCrumbs
        path={[group, "daily-report-preview"]}
        labels={["My Facilities", "Daily Report Preview"]}
      />
      <main className="daily-report-preview-page">
        <header className="daily-report-preview-heading">
          <div>
            <span className="daily-report-preview-eyebrow">Email activity</span>
            <h1>Daily Report Preview</h1>
            <p>Review the report content and recipients for a selected date.</p>
          </div>
        </header>

        <section className="daily-report-preview-card">
          <form className="daily-report-preview-controls" onSubmit={loadPreview}>
            <label htmlFor="daily-report-date">Report date</label>
            <input
              id="daily-report-date"
              type="date"
              value={date}
              disabled={isLoading}
              onChange={(event) => {
                setDate(event.target.value);
                setPreview(null);
                setError("");
                setSendError("");
                setSentRecipients(null);
              }}
              required
            />
            <button type="submit" className="button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                  Loading preview
                </>
              ) : (
                <>
                  <i className="fa-solid fa-eye" aria-hidden="true" />
                  Preview email
                </>
              )}
            </button>
            {preview && (
              <>
                <button
                  type="button"
                  className="button daily-report-send-button"
                  onClick={sendPreviewEmail}
                  disabled={!preview.willSend || isSending}
                >
                  {isSending ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                      Sending email
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                      Send email
                    </>
                  )}
                </button>
                {sendError && <p className="daily-report-preview-error" role="alert">{sendError}</p>}
                {sentRecipients && (
                  <p className="daily-report-preview-sent" role="status">
                    Sent for {date} to {sentRecipients.join(", ")}.
                  </p>
                )}
              </>
            )}
          </form>

          {error && <p className="daily-report-preview-error" role="alert">{error}</p>}

          {!preview && !isLoading && !error && (
            <div className="daily-report-preview-empty">
              <i className="fa-regular fa-calendar" aria-hidden="true" />
              <p>No report preview is available for this date.</p>
            </div>
          )}

          {isLoading && (
            <div className="daily-report-preview-empty" role="status">
              <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
              <p>Loading report activity…</p>
            </div>
          )}

          {preview && (
            <div className="daily-report-email-preview">
              <div className="daily-report-email-meta">
                <div>
                  <span className="daily-report-email-label">Subject</span>
                  <strong>{preview.subject}</strong>
                </div>
                <div>
                  <span className="daily-report-email-label">Activity</span>
                  <strong>{preview.entryCount} {preview.entryCount === 1 ? "entry" : "entries"}</strong>
                </div>
              </div>

              <div className="daily-report-recipients">
                <span className="daily-report-email-label">Recipients ({preview.recipients.length})</span>
                {preview.recipients.length ? (
                  <p>{preview.recipients.join(", ")}</p>
                ) : (
                  <p>No users are currently opted in to daily report emails.</p>
                )}
              </div>

              {!preview.willSend && (
                <p className="daily-report-preview-not-sending" role="status">
                  This email would not be sent because it has no activity or no opted-in recipients.
                </p>
              )}

              <div className="daily-report-email-body">
                <span className="daily-report-email-label">Email body</span>
                <iframe
                  className="daily-report-email-frame"
                  title="Daily report email preview"
                  sandbox=""
                  srcDoc={getPreviewHtml(preview.body)}
                />
              </div>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
